import React from 'react';
import * as Yup from 'yup';
import { MarketingMessageProps, getImageAspect, getImagePickerDescriptionByDisplay } from './base';
import { FormikProvider, useFormik } from 'formik';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';
import { MuiImagePicker } from 'lib/image-picker';
import { Box, Button, Grid, InputLabel, Typography } from '@mui/material';
import { getFileName } from 'lib/helpers';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { StyledImagePickerDescription } from './DefaultMarketingMessageForm';

interface ILocationItemProperties {
    locationId: string;
    imageUrl: string;
    // overlay text properties
    title?: string;
    subtitle?: string;
    subtitleTwo?: string;
    invertTextColour?: boolean;
    showTextBackgroundBar?: boolean;
}

enum EFormField {
    TITLE = 'title',
    NAME = 'name',
    LOCATION = 'locationId',
    SUBTITLE = 'subtitle',
    SUBTITLE_TWO = 'subtitleTwo',
    INVERT_TEXT_COLOR = 'invertTextColour',
    SHOW_TEXT_BACKGROUND_BAR = 'showTextBackgroundBar',
    IMAGE_URL = 'imageUrl'
}

const REQUIRED_ERROR_MESSAGE = 'This field is required.';

const requiredStringSchema = Yup.string().required(REQUIRED_ERROR_MESSAGE);

const validationSchema = Yup.object().shape({
    [EFormField.IMAGE_URL]: requiredStringSchema.nullable(true),
    [EFormField.NAME]: requiredStringSchema,
    [EFormField.LOCATION]: Yup.string().required(REQUIRED_ERROR_MESSAGE),
    [EFormField.TITLE]: Yup.string().nullable(true),
    [EFormField.SUBTITLE]: Yup.string().nullable(true),
    [EFormField.INVERT_TEXT_COLOR]: Yup.boolean(),
    [EFormField.SHOW_TEXT_BACKGROUND_BAR]: Yup.boolean()
});

export const LocationMessageProperties: React.FC<MarketingMessageProps<ILocationItemProperties>> = ({
    properties,
    contextForm,
    onSubmit,
    locations,
    display,
    name
}) => {
    const handleSubmit = React.useCallback(
        (data: ILocationItemProperties) => {
            onSubmit({
                ...properties,
                ...data
            });
        },
        [onSubmit, properties]
    );
    const formik = useFormik<ILocationItemProperties & { name: string }>({
        initialValues: {
            imageUrl: '',
            name: name ?? '',
            title: undefined,
            locationId: undefined,
            subtitle: undefined,
            subtitleTwo: undefined,
            invertTextColour: false,
            showTextBackgroundBar: false,
            ...properties
        },
        onSubmit: handleSubmit,
        validationSchema
    });

    const makeImageChangeHandler = React.useCallback(
        (changeHandler: (changeEvent: any) => void) => (imageUrl: string) => {
            changeHandler({ target: { value: imageUrl, name: EFormField.IMAGE_URL } });
        },
        []
    );
    const imageChangeHandler = makeImageChangeHandler(formik.handleChange);
    const locationOptions = React.useMemo(() => {
        if (!locations?.length) {
            return [];
        }
        return locations.map(({ _id, title }) => ({ value: _id, label: title }));
    }, [locations]);

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <MuiFormLabel required>Image</MuiFormLabel>
                <MuiImagePicker
                    url={formik.values.imageUrl}
                    onSelect={imageChangeHandler}
                    uploadFileName={getFileName(formik.values.imageUrl)}
                    aspect={getImageAspect(display)}
                    accept={['image/jpeg', 'image/png', 'image/gif']}
                />
                <StyledImagePickerDescription variant="caption">
                    {getImagePickerDescriptionByDisplay(display)}
                </StyledImagePickerDescription>
                {!!formik.errors.imageUrl && (
                    <InputLabel shrink error sx={{ mt: 1.5 }}>
                        {formik.errors.imageUrl}
                    </InputLabel>
                )}
                <Box mt={1}>
                    <TextFormField
                        name={EFormField.NAME}
                        label={<MuiFormLabel required>Reporting Name</MuiFormLabel>}
                        description="This code is used to track the number of marketing module item taps on anchor screen"
                    />
                </Box>
                <Box mt={1}>
                    {locationOptions.length ? (
                        <SelectFormField
                            name={EFormField.LOCATION}
                            label={<MuiFormLabel required>Location</MuiFormLabel>}
                            options={locationOptions}
                        />
                    ) : (
                        <SelectFormField
                            name="LOADING"
                            label={<MuiFormLabel required>Location</MuiFormLabel>}
                            options={locationOptions}
                            disabled
                        />
                    )}
                </Box>
                <Box marginTop={2}>
                    <Typography variant="h6" color="primary">
                        Marketing message overlay
                    </Typography>
                </Box>
                <Box mt={1}>
                    <TextFormField name={EFormField.TITLE} label={<MuiFormLabel>Title</MuiFormLabel>} />
                </Box>
                <Box mt={2}>
                    <TextFormField name={EFormField.SUBTITLE} label={<MuiFormLabel>Subtitle</MuiFormLabel>} />
                </Box>
                <Box mt={2}>
                    <TextFormField
                        name={EFormField.SUBTITLE_TWO}
                        label={<MuiFormLabel>Subtitle two</MuiFormLabel>}
                    />
                </Box>
                <Box mt={2}>
                    <SwitchFormField
                        name={EFormField.INVERT_TEXT_COLOR}
                        label={<MuiFormLabel>Invert text colour</MuiFormLabel>}
                    />
                </Box>
                <Box mt={2}>
                    <SwitchFormField
                        name={EFormField.SHOW_TEXT_BACKGROUND_BAR}
                        label={<MuiFormLabel>Show text background bar</MuiFormLabel>}
                    />
                </Box>
                <Box mt={2}>
                    <Grid container spacing={2}>
                        {contextForm()}
                    </Grid>
                </Box>

                <Box display="flex" flex={1} justifyContent="flex-end" mt={1.5}>
                    <Button variant="contained" color="primary" type="submit" disabled={!formik.isValid}>
                        Submit
                    </Button>
                </Box>
            </form>
        </FormikProvider>
    );
};
